import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Link,
  Card,
  CardContent,
  TextField,
} from "@mui/material";
import OtpVerificationService from "../../services/otpVerification-service";
import forgetPasswordService from "../../services/forget-password-service";
import { showToast } from "../../components/toastNotification/index";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../globalState/allReducers";

const OtpVerification: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { email } = location.state as { email: string };
  const [otp, setOtp] = useState<string[]>(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState<number>(60);
  const [canResend, setCanResend] = useState<boolean>(false);
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null);

  const { t } = useTranslation();

  const recaptchaDetails = useSelector((state: RootState) => state.authReducer);

  const handleChange = (value: string, index: number) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-input-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleKeyPress = (
    e: React.KeyboardEvent<HTMLDivElement>,
    index: number
  ) => {
    const target = e.target as HTMLInputElement;
    if (e.key === "Backspace" && !target.value && index > 0) {
      const prevInput = document.getElementById(`otp-input-${index - 1}`);
      prevInput?.focus();
    }
  };
  const startTimer = () => {
    if (intervalId) clearInterval(intervalId);
    setCanResend(false);
    setTimer(60);

    const id = setInterval(() => {
      setTimer((prev) => {
        if (prev === 1) {
          clearInterval(id);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    setIntervalId(id);
  };

  useEffect(() => {
    startTimer();
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  const handleSubmit = () => {
    const otpCode = otp.join("");
    const payload = { otp: otpCode };

    OtpVerificationService.verifyOTPforForgetPassAPI(email, payload)
      .then((response) => {
        console.log(response.status);
        if (
          response &&
          response?.data.message === "OTP verified successfully"
        ) {
          showToast(t(response?.data.message), "success");
          navigate("/createPassword", {
            state: { email: email },
          });
        } else {
          showToast(t(response?.data.message), "error");
        }
      })
      .catch((error) => {
        console.error(t("OTP Verification Error:"), error);
      });
  };
  const handleResendCode = () => {
    const inputData = {
      email: email,
      recaptcha_response: recaptchaDetails?.recaptchaCode,
    };
    forgetPasswordService
      .resetPasswordAPI(inputData)
      .then((response) => {
        if (
          response &&
          response?.data.message === "Reset password email sent"
        ) {
          showToast(t("A new OTP code has been sent."), "success");
          setTimer(60);
          setCanResend(false);
          startTimer();
        } else {
          showToast(t(response?.data.message), "error");
        }
      })
      .catch((error) => {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Something went wrong";
        showToast(errorMessage, "error");
      });
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            mb: 13,
            mt: 3,
          }}
        >
          <Card
            sx={{
              minWidth: { xs: 200, sm: 300, md: 400 },
              maxWidth: { xs: 350, sm: 500, md: 500 },
              m: 2,
              py: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              borderRadius: "40px",
              border: "1px solid #DFDFDF",
              opacity: "1px",
              boxShadow: "none",
            }}
          >
            <CardContent sx={{ textAlign: "center", width: "80%" }}>
              <Typography
                variant="h4"
                color="#0464AB;"
                gutterBottom
                sx={{
                  fontSize: 24,
                  letterSpacing: "1px",
                  marginBottom: "0px",
                }}
                className="h4"
              >
                {t("Verification")}
              </Typography>
              <Typography
                variant="subtitle1"
                gutterBottom
                fontSize={12}
                fontWeight={540}
                sx={{
                  borderBottom: "1px solid #DFDFDF",
                  p: 1,
                  color: "#000000",
                }}
              >
                {t("Enter your OTP code number")}
              </Typography>

              <Box sx={{ pt: 1 }}>
                <form>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    mb={2}
                    sx={{ margin: "10px" }}
                  >
                    {otp.map((value, index) => (
                      <TextField
                        key={index}
                        id={`otp-input-${index}`}
                        type="text"
                        inputProps={{
                          maxLength: 1,
                          style: { textAlign: "center", fontSize: "24px" },
                        }}
                        value={value}
                        onChange={(e) => handleChange(e.target.value, index)}
                        onKeyDown={(e) => handleKeyPress(e, index)}
                        variant="outlined"
                        sx={{
                          width: 50,
                          height: 50,
                          mx: 0.5,
                          "& .MuiInputBase-input.MuiOutlinedInput-input": {
                            height: "0.4375em",
                            padding: {
                              xs: "16.5px 1px",
                              sm: "16.5px 14px",
                              md: "16.5px 14px",
                            },
                          },
                        }}
                      />
                    ))}
                  </Box>
                  <Button
                    variant="contained"
                    disableElevation
                    onClick={handleSubmit}
                    sx={{
                      borderRadius: "21px",
                      backgroundColor: "#0464AB",
                      width: 120,
                      height: 35,
                      fontSize: "14px",
                      fontWeight: "600",
                      textTransform: "none",
                      marginTop: "10px",
                      "&:hover": {
                        backgroundColor: "#FBB522",
                        color: "#022E4C",
                        fontWeight: "600",
                        borderColor: "#FBB522",
                      },
                    }}
                  >
                    {t("Verify")}
                  </Button>
                  {canResend ? (
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      fontSize={12}
                      sx={{
                        pb: 2,
                        fontWeight: 600,
                        color: "#575757",
                        marginTop: "30px",
                        lineHeight: 2,
                      }}
                    >
                      {t("Didn't receive a code?")}
                      <Box
                        component="span"
                        sx={{ display: "block" }}
                      >
                        <Link
                          onClick={handleResendCode}
                          sx={{
                            color: "#1976d2",
                            fontWeight: "bold",
                            cursor: "pointer",
                          }}
                        >
                          {t("Resend Code")}
                        </Link>
                      </Box>
                    </Typography>
                  ) : null}
                </form>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </>
  );
};

export default OtpVerification;
